import { Constants } from '@homegauge/vue';
import Moment from 'moment';

/**
 * Determines if required Estimate data is stored in state.
 * DOES NOT validate data - that is the responsibility of the
 * inserting party.
 *
 * @param {VuexState} state
 * @returns {boolean} True/False based on if expected Estimate data is stored
 */
const hasEstimate = state => {
  const { servicesComponentData } = state;

  if (!servicesComponentData || !servicesComponentData.estimate) {
    return false;
  }

  const { estimate } = servicesComponentData;
  return !!(
    (estimate.invoiceItems && estimate.invoiceItems.length > 0)
    || (estimate.total && estimate.total > 0)
  );
};

/**
 * Determines if required Appointment data is stored in state.
 * DOES NOT validate data - that is the responsibility of the
 * inserting party.
 *
 * @param {VuexState} state
 * @returns {boolean} True/False based on if expected Appointment data is stored
 */
const hasTime = state => {
  const { dateComponentData } = state;

  if (!dateComponentData) {
    return false;
  }

  return !!(dateComponentData.inspectorId
    && dateComponentData.fullName
    && dateComponentData.start
    && dateComponentData.end
    && dateComponentData.time
    && dateComponentData.chosenDate);
};

/**
 * Determines if required Location data is stored in state.
 * DOES NOT validate data - that is the responsibility of the
 * inserting party.
 *
 * @param {VuexState} state
 * @returns {boolean} True/False based on if expected Location data is stored
 */
const hasLocation = state => {
  const { locationComponentData } = state;

  if (!locationComponentData) {
    return false;
  }

  return !!(locationComponentData.address1
    && locationComponentData.city
    && locationComponentData.state
    && locationComponentData.postalCode
    && locationComponentData.country);
};

/**
 * Determines if optional Location Property Details are stored in state.
 * DOES NOT validate data - that is the responsibility of the
 * inserting party.
 *
 * @param {VuexState} state
 * @returns {boolean} True/False based on if there are
 * truthy values in Location Property Details
 */
const hasLocationPropertyDetails = state => {
  const { locationComponentData } = state;

  if (!locationComponentData) {
    return false;
  }

  return state.appointmentCustomFields.some(field => !!locationComponentData[field.id]);
};

// @TODO remove this, it's a direct output of state
// `reachedSummary` would be better suited to the Step module
const reachedSummary = state => state.reachedSummary;

/* eslint-disable no-unused-vars,sonarjs/cognitive-complexity */
const buildScheduleRequest = (state, getters, rootState) => {
  const data = {};

  /**
   * sending local time as a patch until timezone issues are resolved.
   * should be changed back to UTC ISO in future
   */
  const start = Moment(`${state.dateComponentData.chosenDate} ${state.dateComponentData.time}`).format();
  const end = Moment(`${state.dateComponentData.chosenDate} ${state.dateComponentData.end}`).format();
  const fieldMappings = {
    isPoweron: /power/i,
    isWaterOn: /water/i,
    yearBuilt: /year/i,
    isGasOn: /gas/i,
    squareFootage: /square footage/i,
    bedrooms: /bedrooms/i,
    bathrooms: /full bathrooms/i,
    halfBathrooms: /half bathrooms/i,
    mlsNumber: /mls/i,
    isOccupied: /occupied/i,
  };
  const defaultCustomFields = state.appointmentCustomFields.filter(cf => cf.default);
  const defaultFieldsPayload = defaultCustomFields.reduce((acc, cf) => {
    const matchingField = Object.entries(fieldMappings)
      .find(([_, regex]) => regex.test(cf.description));
    if (matchingField) {
      acc[matchingField[0]] = state.locationComponentData[cf.id] || '';
    }
    return acc;
  }, {});

  data.request = {
    startTime: start,
    endTime: end,
    location: {
      address1: state.locationComponentData.address1,
      address2: state.locationComponentData.address2,
      city: state.locationComponentData.city,
      state: state.locationComponentData.state,
      postalCode: state.locationComponentData.postalCode,
      country: state.locationComponentData.country,
      latitude: state.locationComponentData.latitude,
      longitude: state.locationComponentData.longitude,
    },
    customerComments: state.locationComponentData.additionalInstructions,
    legacyInspectorId: state.dateComponentData.inspectorId,
    ...defaultFieldsPayload,
    services: state.servicesComponentData.selectedServices
      .map(selectedService => ({
        quantity: selectedService.quantity || 1,
        serviceId: selectedService.id,
      })),
    customFields: state.appointmentCustomFields
      .filter(cf => !cf.default && !cf.internal).map(field => {
        const { id } = field;
        const value = state.locationComponentData[id] || '';
        return {
          definitionId: id,
          value,
        };
      }),
    buyers: [],
    buyerReps: [],
    owners: [],
    ownerReps: [],
  };

  const role = state.contactComponentData?.role?.toLowerCase();
  const personType = rootState?.Auth?.person?.type;
  const personLegacyId = rootState?.Auth?.person?.legacyId;
  if (role && personType && personLegacyId) {
    if (personType === Constants.AccountTypes.CUSTOMER) {
      data.request[`${role}s`].push(personLegacyId);
    } else {
      data.request[`${role}Reps`].push(personLegacyId);
    }
  }
  state.contactComponentData.contacts.forEach(contact => {
    const contactType = contact?.type;
    const contactLegacyId = contact?.legacyId;
    if (role && contactType && contactLegacyId) {
      if (contactType === Constants.AccountTypes.CUSTOMER) {
        data.request[`${role}s`].push(contactLegacyId);
      } else {
        data.request[`${role}Reps`].push(contactLegacyId);
      }
    }
  });

  return data.request;
};
/* eslint-enable no-unused-vars,sonarjs/cognitive-complexity */

const getCustomFields = state => {
  const { appointmentCustomFields } = state || [];

  return appointmentCustomFields;
};

export default {
  hasEstimate,
  hasTime,
  hasLocation,
  hasLocationPropertyDetails,
  reachedSummary,
  buildScheduleRequest,
  getCustomFields,
};
